// src/services/recruitService.js
// 인원 모집 게시판
// 21차: joinRecruit / leaveRecruit 직접 참가 함수 제거
//       requestJoinRecruit → approveParticipant 승인 시스템으로 완전 통일
// 23차: 신고 중복 체크 서버사이드 보완 (submitReportSafe)
//       일괄 예약 트랜잭션 헬퍼 (bulkReserveForRecruit)
//       recruit 타입 예약 조회 헬퍼 (getMyRecruitReservations)
// 30차: processExpiredRecruits — noShowProcessed: true 필터 추가 (중복 처리 방지)
// 32차: applyNoShowPenalty — 제재 후 인앱 알림 발송
// 33차: notifyExpiringPoints — 포인트 D-7/D-1 임박 인앱 알림
//        notifyUnreviewedReservations — 리뷰 미작성 유도 인앱 알림
//        notifyStoreDeleted — 매장 soft-delete 시 예약자 일괄 알림
// 36차: notifyExpiringPoints / notifyUnreviewedReservations / notifyStoreDeleted
//        → Cloud Functions(httpsCallable) 래퍼로 교체
//        클라이언트에서 타인 uid notifications 생성 불가 (35차 Rules)
// 52차: 폴링 → 실시간 리스너 전환 (listenUnreadNotificationCount / listenMyRecruitsPendingCount)
// 72차: ADMIN_NOTIF_TYPES 신규 + getUnreadNotificationCount/listenUnreadNotificationCount에
//       adminOnly 옵션 추가 — 매장관리자용 알림 뱃지가 고객용 타입까지 세어 안 지워지는
//       뱃지가 되던 문제 방지 (NotificationsScreen 목록 필터링과 짝을 맞춤)
// 53차: 포인트 보안 이관
//        grantPoints   → CF grantPointsCF 래퍼 (서버 검증: reason 화이트리스트 + amount 상한)
//        expirePoints  → DEPRECATED stub (서버 스케줄러 scheduledExpirePoints가 처리)
//        usePointsFIFO → CF usePointsFIFO_CF 래퍼 (서버 잔액 트랜잭션 검증)
// 71차: bulkReserveForRecruit — reservationService.buildReservationPayload로 통일
//       (storeId/branchName/originalPrice가 빠져 있던 문제 해결, storeId는 productId에서 역산)
// 74차: createRecruit에 crewId 파라미터 추가 — 크루원 모집으로 등록 시 어느 크루 소속
//       모집글인지 표시. 크루 게시판 자동 연동은 RecruitWriteScreen.js가 addCrewRecruitLink
//       (crewService.js)를 별도로 호출해서 처리(이 함수는 recruits 문서 저장만 책임)
// 74차: authorCrewName 스냅샷 추가 — 작성자 닉네임 옆 [크루명] 태그 표시용
// 75차: 모집 참가신청/수락/거절 알림이 전부 조용히 실패하고 있던 버그 수정 —
// requestJoinRecruit/approveParticipant/rejectParticipant가 클라이언트에서 "본인이 아닌
// 남"의 uid로 notifications를 직접 addDoc하고 있었는데, firestore.rules는
// uid==auth.uid만 create를 허용해서 항상 permission-denied(무음 catch라 겉으론 티가
// 안 났음). functions/index.js의 onRecruitUpdated 트리거로 이관하고 클라이언트 쪽
// addDoc은 제거. 같은 조사에서 processExpiredRecruits/applyNoShowPenalty(둘 다 52차에
// 서버 스케줄러로 이관된 뒤 아무도 안 부르는 죽은 코드)/createNotification(호출부 없음)도
// 발견해 함께 삭제.
import {
  collection, doc, addDoc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, limit, arrayUnion, arrayRemove,
  serverTimestamp, increment, writeBatch, onSnapshot, runTransaction,
  getCountFromServer,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, functionsAsia } from './firebase';
import { buildReservationPayload, normalizeReservation, buildSlotId, mapSlotConflictError } from './reservationService';
import { parseProductId } from './productService';

const COL = 'recruits';

// ─── 모집글 생성 ────────────────────────────────────────────
export const createRecruit = async ({
  uid, authorName,
  authorCrewName = null,
  title,
  productId,
  productName,
  storeName,
  imageUrl,
  region,
  scheduledDate,
  scheduledTime,
  totalSlots,
  description,
  kakaoLink,
  difficulty,
  genres,
  bookingFormat = 'room', // 80차 후속: 모집 목록 필터/뱃지(방탈출·체험형 구분)용
  isAdultOnly = false, // 80차 후속: 19금 테마 모집 — 목록/상세 뱃지 + 참가 나이경고용 스냅샷
  crewId = null, // 74차: 크루원 모집으로 등록 시 크루 게시판 자동 연동용
  privateChatEnabled = false, // 80차 계속: 승인된 참가자 전용 비공개 채팅(recruits/{id}/chat) 사용 여부
}) => {
  const data = {
    uid, authorName,
    authorCrewName,
    title,
    productId:    productId || null,
    productName,
    storeName:    storeName || '',
    imageUrl:     imageUrl  || '',
    region,
    scheduledDate,
    scheduledTime: scheduledTime || '',
    totalSlots:   Number(totalSlots),
    currentSlots: 1,
    participants: [uid],
    pendingParticipants:  [],
    rejectedParticipants: [],
    description,
    kakaoLink:    kakaoLink || null,
    difficulty:   difficulty || null,
    genres:       genres || [],
    bookingFormat,
    isAdultOnly,
    status:       'open',
    viewCount:    0,
    bulkReserved: false,
    crewId,
    privateChatEnabled,
    createdAt:    serverTimestamp(),
    updatedAt:    serverTimestamp(),
  };
  const ref = await addDoc(collection(db, COL), data);
  return { id: ref.id, ...data };
};

// ─── 단건 조회 + 조회수 증가 ────────────────────────────────
export const getRecruitById = async (id) => {
  if (!id) return null;
  const snap = await getDoc(doc(db, COL, id));
  if (!snap.exists()) return null;
  updateDoc(doc(db, COL, id), { viewCount: increment(1) }).catch(() => {});
  return { id: snap.id, ...snap.data() };
};

// ─── 참가자 프로필 조회 ─────────────────────────────────────
// 78차: firestore.rules의 users list가 관리자 전용이라 이 직접 쿼리가 항상
// permission-denied로 조용히 실패하고 있었음(실기기 테스트로 발견, getCrewMemberProfilesCF와
// 동일 유형) — 참가자 목록은 공개 데이터라 작성자 제한 없는 CF로 이관.
// 78차 64차: [보안 수정] uid 배열을 클라이언트가 그대로 넘기던 방식이 실제 참가자인지
// 검증 없이 임의 uid의 개인정보 전체를 노출하는 취약점이었음 — recruitId만 넘기고
// 실제 참가자 목록/반환 필드 제한은 서버(CF)가 전담하도록 변경.
export const getParticipantProfiles = async (recruitId) => {
  if (!recruitId) return [];
  const cf = httpsCallable(functionsAsia, 'getRecruitParticipantProfilesCF');
  const result = await cf({ recruitId });
  return result.data.profiles || [];
};

// ─── 참가 취소 (수락된 참가자) ──────────────────────────────
// 78차 43차: approveParticipant와 동일한 유형의 동시성 버그 — getDoc()으로 읽은
// currentSlots를 그대로 -1 해서 updateDoc()하는 read-then-write라, 두 참가자가
// 거의 동시에 나가면 둘 다 같은 currentSlots를 읽어 하나만 반영되는 드리프트가
// 가능했음(participants 배열 자체는 arrayRemove라 안전하지만 currentSlots/status는
// 아니었음). runTransaction으로 교체.
export const leaveRecruit = async (recruitId, uid) => {
  const ref = doc(db, COL, recruitId);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    if (data.uid === uid) throw new Error('작성자는 취소할 수 없습니다. 모집글을 삭제해주세요.');
    if (!(data.participants || []).includes(uid)) return; // 이미 나간 상태 — 중복 처리 무시
    // 80차 계속: [버그 수정] bulkReserved(실제 예약 확정) 이후엔 이 함수가 participants
    // 배열만 빼고 실제 reservations 문서는 전혀 안 건드려서, 로스터엔 "나감"으로 보이는데
    // 물리적 예약은 그 사람 명의로 그대로 남아있는 불일치가 생겼음(RecruitDetailScreen.js
    // 재검토 중 발견 — "참가 취소" 버튼이 이 시점 이후에도 계속 눌려있었음). 확정 후엔
    // 이 경로 자체를 막고, 대신 requestSeatTransfer()(→ transferRecruitSeatCF가 실제
    // 예약 문서까지 같이 재배정)로 유도.
    if (data.bulkReserved) throw new Error('이미 예약이 확정된 모임은 참가취소 대신 자리 양도를 이용해주세요.');
    transaction.update(ref, {
      participants: arrayRemove(uid),
      currentSlots: Math.max(1, (data.currentSlots || 1) - 1),
      status: 'open',
      updatedAt: serverTimestamp(),
    });
  });
};

// ─── 자리 양도 (80차 계속) ───────────────────────────────────
// 모임장이 아니라 "참여인원"이 확정된 예약을 못 쓰게 됐을 때, 그룹 전체를 취소하는 대신
// 자기 자리 하나만 넘기는 기능. 본인이 openSeatUid를 셋(firestore.rules 자기서비스
// 허용)해 "이 자리 뺄게요" 신호만 보내고, 실제 예약 문서 재배정은 모임장이 지원자 중
// 한 명을 골라 승인하는 시점에 transferRecruitSeatCF(Admin SDK)가 처리한다.
export const requestSeatTransfer = async (recruitId, uid) => {
  const ref = doc(db, COL, recruitId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('모집글을 찾을 수 없습니다.');
  const data = snap.data();
  if (!data.bulkReserved) throw new Error('아직 예약이 확정되지 않았습니다.');
  if (!(data.participants || []).includes(uid)) throw new Error('참가자만 요청할 수 있습니다.');
  if (data.openSeatUid && data.openSeatUid !== uid) throw new Error('이미 다른 자리 양도가 진행 중입니다.');
  await updateDoc(ref, { openSeatUid: uid, updatedAt: serverTimestamp() });
};

export const cancelSeatTransfer = async (recruitId) => {
  const ref = doc(db, COL, recruitId);
  await updateDoc(ref, { openSeatUid: null, updatedAt: serverTimestamp() });
};

// ─── 자리 양도 승인 (모임장 전용) ─────────────────────────────
export const approveSeatTransfer = async (recruitId, incomingUid) => {
  const cf = httpsCallable(functionsAsia, 'transferRecruitSeatCF');
  await cf({ recruitId, incomingUid });
};

// ─── 모임 후 매너 평가 (80차 계속) ───────────────────────────
// 노쇼 이력(부정적 신호)만 있고 좋은 참가자였다는 신호가 없던 비대칭을 보완 — 모임
// 예정일이 지난 뒤 참가자끼리 "또 하고 싶어요"를 남길 수 있게 함. CF 없이 클라이언트
// 직접 create만으로 구현: recruitId_rater_ratee 결정론적 doc ID라 같은 조합으로 두 번
// 쓰면 Firestore가 이를 "update"로 취급하고 firestore.rules가 update를 전면 거부해서
// 자연히 중복이 막힘(집계 카운터를 별도로 관리할 서버 코드가 필요 없음). 집계는
// getCountFromServer로 그때그때 조회(크루 랭킹의 listTopCrews와 동일하게, 아직 트래픽이
// 적은 단계라 별도 서버 집계 없이 충분하다는 판단).
const ratingDocId = (recruitId, raterUid, rateeUid) => `${recruitId}_${raterUid}_${rateeUid}`;

export const submitPeerRating = async (recruitId, raterUid, rateeUid) => {
  if (raterUid === rateeUid) throw new Error('본인은 평가할 수 없습니다.');
  const ref = doc(db, 'recruitRatings', ratingDocId(recruitId, raterUid, rateeUid));
  await setDoc(ref, { recruitId, raterUid, rateeUid, createdAt: serverTimestamp() });
};

export const getMyPeerRatings = async (recruitId, raterUid) => {
  const snap = await getDocs(query(
    collection(db, 'recruitRatings'),
    where('recruitId', '==', recruitId),
    where('raterUid', '==', raterUid),
  ));
  return snap.docs.map(d => d.data().rateeUid);
};

export const getMannerCount = async (uid) => {
  const snap = await getCountFromServer(query(
    collection(db, 'recruitRatings'),
    where('rateeUid', '==', uid),
  ));
  return snap.data().count;
};

// ─── 모집글 수정 ────────────────────────────────────────────
// 79차: [버그 수정] "이미 승인된 참가자 수보다 정원을 적게 줄일 수 없다"는 검증이
// `RecruitWriteScreen.js`의 화면단(handleSubmit, 78차43차)에만 있고 이 함수 자체엔
// 없었음 — 화면을 거치지 않고 이 함수를 직접 호출하면 `currentSlots > totalSlots`인
// 모순 상태를 만들 수 있고, `bulkReserveForRecruit()`이 `recruit.totalSlots`를 그대로
// 인원별 가격표 조회 키로 쓰기 때문에 정원을 실제 참가자 수보다 작게 줄여두면 실제
// 인원보다 적은 가격으로 예약금액이 정정되는(과소청구) 경로로 이어질 수 있음
// (전수 정독으로 발견) — 화면단 검증과 별개로 서버측(클라 SDK 직접 호출) 방어 추가.
export const updateRecruit = async (recruitId, {
  title, productId, productName, storeName, imageUrl,
  region, scheduledDate, scheduledTime, totalSlots,
  description, kakaoLink, difficulty, genres, bookingFormat = 'room',
  isAdultOnly = false, // 80차 후속: 19금 테마 모집 스냅샷 — 수정 시에도 최신 테마 값 반영
  privateChatEnabled = false, // 80차 계속: 작성 후에도 켜고 끌 수 있게 수정 시에도 반영
}) => {
  const ref = doc(db, COL, recruitId);
  const snap = await getDoc(ref);
  const currentSlots = snap.data()?.currentSlots || 0;
  if (Number(totalSlots) < currentSlots) {
    throw new Error(`이미 승인된 참가자가 ${currentSlots}명 있어 정원을 그보다 적게 줄일 수 없어요.`);
  }
  await updateDoc(ref, {
    title, productId: productId || null, productName,
    storeName: storeName || '',
    imageUrl: imageUrl || '',
    region, scheduledDate,
    scheduledTime: scheduledTime || '',
    totalSlots: Number(totalSlots),
    description: description || '',
    kakaoLink: kakaoLink || null,
    difficulty: difficulty || null,
    genres: genres || [],
    bookingFormat, // 80차 후속: 모집 목록 필터/뱃지(방탈출·체험형 구분)용
    isAdultOnly,
    privateChatEnabled,
    updatedAt: serverTimestamp(),
  });
};

// ─── 참가자 전용 비공개 채팅 ────────────────────────────────────
// 80차 계속: firestore.rules가 recruits.participants 멤버십 + privateChatEnabled를
// 이미 서버에서 강제하므로, 여기선 그 조건에 맞는 write/read만 시도하면 됨(클라이언트
// 재검증은 UI 노출 조건에서만 처리 — RecruitDetailScreen.js 참고).
export const addRecruitChatMessage = ({ recruitId, uid, nickname, text }) =>
  addDoc(collection(db, COL, recruitId, 'chat'), {
    uid, nickname: nickname || '익명', text, createdAt: serverTimestamp(),
  });

export const listenRecruitChatMessages = (recruitId, cb) => {
  const q = query(collection(db, COL, recruitId, 'chat'), orderBy('createdAt', 'asc'));
  return onSnapshot(q, snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => cb([]));
};

// ─── 모집글 삭제 ────────────────────────────────────────────
// 79차: [버그 수정] 크루원 모집으로 등록된 글(addCrewRecruitLink)이 삭제돼도 크루
// 게시판의 연동 카드(crewPosts, type:'recruit_link')는 그대로 남아 탭하면 "게시물을
// 찾을 수 없습니다"만 뜨는 dead-end로 영구히 남았음(fork로 발견) — onRecruitUpdated
// CF는 있지만 삭제 트리거는 없었음. 그 카드의 uid는 모집글 작성자 본인이라(같은
// 사람만 이 deleteRecruit을 호출할 수 있음, RecruitDetailScreen이 작성자에게만
// 삭제 버튼을 노출) firestore.rules의 crewPosts delete(resource.data.uid==auth.uid)를
// 그대로 통과 — 클라이언트에서 같이 정리.
export const deleteRecruit = async (recruitId) => {
  // 80차 계속(재검토로 발견): chat 서브컬렉션은 삭제 시 함께 안 지워짐(고아 데이터로 남음).
  // rules상 delete가 본인 메시지만 가능해 클라이언트에서 참가자 전원 메시지를 정리할
  // 방법이 없고(다른 참가자 메시지는 permission-denied), 부모 문서 삭제 후엔 rules의
  // get()이 실패해 어차피 읽기/쓰기 전부 막혀 기능상 무해한 저장공간 누수로만 남음 —
  // 제대로 정리하려면 Admin SDK 기반 삭제 트리거가 필요해 낮은 우선순위로 보류.
  await deleteDoc(doc(db, COL, recruitId));
  const linkSnap = await getDocs(query(
    collection(db, 'crewPosts'),
    where('type', '==', 'recruit_link'),
    where('recruitId', '==', recruitId),
  ));
  await Promise.all(linkSnap.docs.map(d => deleteDoc(d.ref).catch(() => {})));
};

// ─── 참가 신청 (pending) ────────────────────────────────────
export const requestJoinRecruit = async (recruitId, uid) => {
  const ref = doc(db, COL, recruitId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('모집글을 찾을 수 없습니다.');
  const data = snap.data();
  if (data.uid === uid) throw new Error('작성자는 신청할 수 없습니다.');
  if (data.participants?.includes(uid)) throw new Error('이미 참가 중입니다.');
  if ((data.pendingParticipants || []).includes(uid)) throw new Error('이미 신청했습니다.');
  // 80차 계속: openSeatUid(자리 양도 요청)가 켜져 있으면 정원이 이미 찬(currentSlots>=totalSlots)
  // 상태라도 그 한 자리를 채울 지원자는 받아야 함 — transferRecruitSeatCF가 이 지원자 중
  // 한 명을 승인해 예약 소유자만 교체하고 currentSlots는 그대로 유지.
  if (data.currentSlots >= data.totalSlots && !data.openSeatUid) throw new Error('모집 인원이 마감되었습니다.');
  await updateDoc(ref, {
    pendingParticipants: arrayUnion(uid),
    updatedAt: serverTimestamp(),
  });
  // 75차: 여기서 클라이언트가 직접 작성자 앞으로 알림을 addDoc하던 코드는 제거 —
  // firestore.rules(notifications create는 uid==auth.uid만 허용)에 항상 막혀서
  // permission-denied로 조용히 실패하고 있었음. functions/index.js의 onRecruitUpdated
  // 트리거(recruits 문서의 pendingParticipants 변화 감지)로 이관.
};

// ─── 신청 취소 (pending 취소) ────────────────────────────────
export const cancelJoinRequest = async (recruitId, uid) => {
  const ref = doc(db, COL, recruitId);
  await updateDoc(ref, {
    pendingParticipants: arrayRemove(uid),
    updatedAt: serverTimestamp(),
  });
};

// ─── 신청 수락 (작성자) ──────────────────────────────────────
// 78차: getDoc()으로 읽고 updateDoc()으로 쓰는 read-then-write라 동시성 방어가 전혀
// 없었음(전수조사로 발견) — 작성자가 두 신청을 거의 동시에 승인하면(더블탭, 또는 두
// 신청을 빠르게 연속 승인) 둘 다 같은 currentSlots를 읽어 같은 값+1로 덮어쓰면서
// participants만 arrayUnion으로 누적돼, currentSlots는 정원 이내인데 실제
// participants.length는 totalSlots를 초과하는 상태가 될 수 있었음(status도 'full'로
// 안 바뀌어 초과 승인이 계속 가능). 이 participants 배열이 그대로 bulkReserveForRecruit의
// 실제 예약 인원이 되므로, 방 정원을 넘는 인원이 예약되는 결과로 이어질 수 있음.
// reservationService.js의 예약 트랜잭션과 동일한 방식(runTransaction)으로 원자성 확보,
// 이미 정원이 찼으면 승인 자체를 거부.
export const approveParticipant = async (recruitId, uid) => {
  const ref = doc(db, COL, recruitId);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) throw new Error('모집글을 찾을 수 없습니다.');
    const data = snap.data();
    if ((data.participants || []).includes(uid)) return; // 이미 승인된 신청 — 중복 승인 무시
    const newSlots = (data.currentSlots || 0) + 1;
    if (newSlots > data.totalSlots) throw new Error('이미 정원이 가득 찼습니다.');
    transaction.update(ref, {
      pendingParticipants: arrayRemove(uid),
      participants:        arrayUnion(uid),
      currentSlots:        newSlots,
      status:              newSlots >= data.totalSlots ? 'full' : 'open',
      updatedAt:           serverTimestamp(),
    });
  });
  // 75차: 알림 addDoc 제거 — onRecruitUpdated 트리거로 이관(위 requestJoinRecruit 참고)
};

// ─── 신청 거절 (작성자) ──────────────────────────────────────
export const rejectParticipant = async (recruitId, uid) => {
  const ref = doc(db, COL, recruitId);
  await updateDoc(ref, {
    pendingParticipants:  arrayRemove(uid),
    rejectedParticipants: arrayUnion(uid),
    updatedAt: serverTimestamp(),
  });
  // 75차: 알림 addDoc 제거 — onRecruitUpdated 트리거로 이관(위 requestJoinRecruit 참고)
};

// ─── pending 참가자 프로필 조회 ──────────────────────────────
// 78차: 위 getParticipantProfiles와 동일한 이유로 permission-denied 조용히 실패 —
// "참가 신청 대기" 섹션(RecruitDetailScreen.js)이 대기자가 있어도 한 번도 렌더링된 적이
// 없었을 가능성이 높음(실기기 테스트로 발견). 신청 대기자는 작성자만 봐야 하므로
// uid 목록이 아니라 recruitId를 넘겨 서버에서 작성자 여부를 직접 검증하는 CF로 이관 —
// 호출부(RecruitDetailScreen.js)도 recruitId를 넘기도록 같이 수정.
export const getPendingProfiles = async (recruitId) => {
  if (!recruitId) return [];
  const cf = httpsCallable(functionsAsia, 'getRecruitPendingProfilesCF');
  const result = await cf({ recruitId });
  return result.data.profiles || [];
};

// ─────────────────────────────────────────────────────────────
// ── 23차 추가 ────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────

// ─── 신고 영구 중복 방지 체크 ────────────────────────────────
export const checkAlreadyReported = async (uid, recruitId) => {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return false;
  const arr = snap.data().reportedRecruits || [];
  return arr.includes(recruitId);
};

// ─── 신고 후 users 문서에 recruitId 기록 ─────────────────────
export const addReportedRecruit = async (uid, recruitId) => {
  await updateDoc(doc(db, 'users', uid), {
    reportedRecruits: arrayUnion(recruitId),
  });
};

// ─── 신고 중복·어뷰징 방지 강화 버전 ────────────────────────
export const submitReportSafe = async ({
  recruitId,
  reporterUid,
  reporterName,
  targetUid,
  targetName,
  reason,
  title,
}) => {
  const alreadyReported = await checkAlreadyReported(reporterUid, recruitId);
  if (alreadyReported) throw new Error('ALREADY_REPORTED');

  // 78차: todayStr()가 UTC 기준이었고, 거기에 'Z'(UTC)로 하루 경계까지 만들어서 이중으로
  // 어긋나 있었음 — 실제 "하루 5건" 제한이 한국 자정이 아니라 오전 9시를 기준으로 리셋되고
  // 있었던 것. new Date()로 직접 로컬 자정~자정+1일 경계를 만들도록 수정.
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const todayEnd   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const dailySnap  = await getDocs(query(
    collection(db, 'reports'),
    where('reporterUid', '==', reporterUid),
    where('createdAt', '>=', todayStart),
    where('createdAt', '<=', todayEnd),
  ));
  if (dailySnap.size >= 5) throw new Error('DAILY_LIMIT');

  await addDoc(collection(db, 'reports'), {
    recruitId,
    reporterUid,
    reporterName,
    targetUid,
    targetName,
    reason,
    title,
    createdAt: serverTimestamp(),
    status: 'pending',
  });

  await addReportedRecruit(reporterUid, recruitId);
  // 75차: 여기서 신고자가 "피신고자"(본인 아님) 문서의 reportStats.receivedCount를
  // 직접 updateDoc하고 있었는데, firestore.rules의 users update는 본인 문서만 허용해서
  // 항상 permission-denied였음(무음 catch라 티가 안 났음) — 즉 피신고자의 누적 신고
  // 횟수가 신고 시점엔 한 번도 안 올라가고 있었을 가능성이 높음. functions/index.js의
  // onNewReport 트리거(Admin SDK)로 이관.
};

// ─── 일괄 예약 (방탈출 전용) ──────────────────────────────────
// 80차 후속: [버그 수정, 심각] 기존엔 writeBatch로 N개 예약 문서(참가자별 uid)만 쓰고
// reservationSlots 잠금을 전혀 안 만들었음 — 더 심각하게는, firestore.rules의
// reservations create가 "uid == request.auth.uid"만 허용해서(예외 없음) 작성자 본인이
// 아닌 참가자 명의 문서는 전부 permission-denied였고, writeBatch는 배치 내 문서 하나라도
// 거부되면 전체가 실패하므로 이 기능은 사실상 한 번도 정상 동작한 적이 없었을 것으로
// 추정(에뮬레이터로 재현해 확정). firestore.rules에 isValidRecruitBulkReservation 예외를
// 추가하고, 여기서는 reservationSlots 잠금까지 하나의 트랜잭션으로 원자적으로 처리하도록
// 전환(개별 예약 addReservationWithPoints와 동일한 잠금 패턴).
export const bulkReserveForRecruit = async ({
  recruit,
  participants,
  productName,
  storeName,
  price = 0,
  duration = 60,
}) => {
  if (!recruit?.productId || !recruit?.scheduledDate || !recruit?.scheduledTime) {
    throw new Error('예약에 필요한 테마·날짜·시간 정보가 없습니다.');
  }
  if (!participants?.length) {
    throw new Error('참가자 정보가 없습니다.');
  }

  const now = new Date().toISOString();
  const genre = recruit.genres?.[0] || recruit.genre || null;
  // 71차: recruit엔 storeId가 직접 저장돼 있지 않아 productId에서 역산
  //   (이전엔 이 필드가 아예 빠져 있어 recruit 예약만 storeId 없는 문서였음)
  const storeId = parseProductId(recruit.productId)?.storeId || null;
  const slotRef = doc(db, 'reservationSlots', buildSlotId(recruit.productId, recruit.scheduledDate, recruit.scheduledTime));

  try {
  return await runTransaction(db, async (transaction) => {
    // 67차: transaction.get()으로 읽어야 Firestore가 실제 동시성 충돌을 감지한다
    // 80차 후속: 이 슬롯이 남(다른 uid)의 소유면 이제 not-exists가 아니라 permission-denied가
    // 뜬다(reservationSlots.read가 소유자 전용으로 좁혀짐) — 아래 catch에서 SLOT_TAKEN으로 매핑.
    const slotSnap = await transaction.get(slotRef);
    if (slotSnap.exists()) throw new Error('SLOT_TAKEN');

    const reservationIds = [];
    // 78차 44차: 참가자 수만큼 예약 문서를 만들지만(개별 리뷰/취소/출석 추적 목적上 유지),
    // 서버 가격검증이 각 문서를 전부 "그룹 전체 가격"으로 정정하므로 매출 집계에선 정확히
    // 한 건(첫 참가자)만 revenueDuplicate:false로 남기고 나머지는 true로 표시.
    participants.forEach((p, idx) => {
      const resRef = doc(collection(db, 'reservations'));
      reservationIds.push(resRef.id);
      transaction.set(resRef, buildReservationPayload({
        uid: p.uid,
        productId:  recruit.productId,
        productName: productName || recruit.productName || '',
        storeName:  storeName    || recruit.storeName   || '',
        storeId,
        date: recruit.scheduledDate,
        time: recruit.scheduledTime,
        people: recruit.totalSlots,
        price,
        usedPoints: 0,
        duration,
        genre,
        paymentType: 'recruit',
        recruitId: recruit.id,
        createdAt: now,
        revenueDuplicate: idx > 0,
      }));
    });

    transaction.set(slotRef, {
      reservationId: reservationIds[0], productId: recruit.productId,
      date: recruit.scheduledDate, time: recruit.scheduledTime,
      uid: recruit.uid, createdAt: now,
    });

    transaction.update(doc(db, 'recruits', recruit.id), {
      bulkReserved: true,
      updatedAt:    now,
    });

    return reservationIds;
  });
  } catch (e) {
    throw mapSlotConflictError(e);
  }
};

// 80차 후속: 모집 작성자(또는 매장)가 방탈출 그룹예약 전체를 한 번에 취소 — 참가자
// 개인의 셀프취소는 reservationService.cancelReservation이 이미 차단(공유 잠금을
// 실수로 풀지 않도록). 참가자 전원에게 알림도 서버(CF)가 팬아웃.
export const cancelBulkRoomReservation = async (recruitId) => {
  const cf = httpsCallable(functionsAsia, 'cancelRoomRecruitReservationCF');
  await cf({ recruitId });
};

// 80차 후속: 체험형 그룹예약 취소(작성자 전용) — cancelShowReservationCF는 room과 달리
// recruitId가 아니라 reservationId로 진입한다(정원 카운터를 개별 예약 문서 기준으로
// 다루던 기존 구조를 그대로 유지하기 위함). 작성자는 항상 이 그룹의 참가자이기도 해서
// 본인 소유 예약 문서 하나를 조회해도 firestore.rules(list: uid==auth.uid)에 걸리지
// 않음 — 그 문서 id로 CF를 호출하면 CF 내부에서 recruitId로 형제 전부를 찾아 처리한다.
export const cancelBulkShowReservation = async (recruitId, myUid) => {
  const q = query(collection(db, 'reservations'), where('recruitId', '==', recruitId), where('uid', '==', myUid));
  const snap = await getDocs(q);
  const myDoc = snap.docs.find(d => d.data().cancelled !== true);
  if (!myDoc) throw new Error('취소할 예약을 찾을 수 없습니다.');
  const cf = httpsCallable(functionsAsia, 'cancelShowReservationCF');
  await cf({ reservationId: myDoc.id });
};

// 80차 후속: 체험형(bookingFormat:'show') 테마는 정원을 서버 트랜잭션으로 원자적으로
// 검증해야 해서(reserveShowSlotCF와 동일 이유) 위 클라이언트 writeBatch 대신 CF를 호출.
export const bulkReserveShowForRecruit = async ({
  recruit, productName, storeName, participantUids, duration = 60, genre,
}) => {
  const cf = httpsCallable(functionsAsia, 'bulkReserveShowForRecruitCF');
  const res = await cf({
    recruitId: recruit.id,
    productId: recruit.productId,
    productName: productName || recruit.productName || '',
    storeName: storeName || recruit.storeName || '',
    participantUids,
    duration,
    genre: genre || recruit.genres?.[0] || recruit.genre || null,
  });
  return res.data.reservationIds;
};

// ─── 모집 예약 목록 조회 ─────────────────────────────────────
export const getMyRecruitReservations = async (uid) => {
  const q = query(
    collection(db, 'reservations'),
    where('uid', '==', uid),
    where('paymentType', '==', 'recruit'),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => normalizeReservation({ id: d.id, ...d.data() }))
    .filter(r => r.cancelled !== true);
};

// ─────────────────────────────────────────────────────────────
// ── 26차 추가 ────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────

// ─── 노쇼/신고 제재 여부 확인 ─────────────────────────────────
export const checkRecruitBan = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return { banned: false };
  const data = snap.data();
  if (!data.recruitBanUntil) return { banned: false };
  const banUntil = new Date(data.recruitBanUntil);
  if (banUntil <= new Date()) return { banned: false };
  const isPermanent = data.recruitBanUntil === '9999-12-31T00:00:00.000Z';
  return {
    banned: true,
    banUntil: data.recruitBanUntil,
    noShowCount: data.noShowCount || 0,
    isPermanent,
    reason: data.recruitBanReason || 'noshow', // 75차: 기존 데이터는 전부 노쇼 사유였으므로 기본값 유지
  };
};

// ─── 75차: 신고 처리 기반 모집글 작성 제한 (총관리자 수동 부여) ─────
// 79차: [버그 수정] 이 함수와 형제 함수 applyNoShowPenaltyServer(functions/index.js)가
// 서로의 현재 값을 확인하지 않고 recruitBanUntil/recruitBanReason을 무조건 덮어쓰고
// 있었음(fork로 발견) — 예: 노쇼로 30일 정지 중인 유저가 별건 신고로 1일 제한을 받으면
// 남은 20여 일이 그대로 1일로 단축됨(반대 방향도 동일). 기존 제재가 이번에 부과할
// 기간보다 더 나중까지 유효하면 그걸 유지하도록(더 짧은 쪽으로 절대 덮어쓰지 않게) 수정.
export const applyReportBan = async (uid, days) => {
  const newBanUntil = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
  const userSnap = await getDoc(doc(db, 'users', uid));
  const existing = userSnap.exists() ? userSnap.data() : {};
  const keepExisting = existing.recruitBanUntil && new Date(existing.recruitBanUntil) > new Date(newBanUntil);
  const banUntil = keepExisting ? existing.recruitBanUntil : newBanUntil;
  await updateDoc(doc(db, 'users', uid), {
    recruitBanUntil: banUntil,
    recruitBanReason: keepExisting ? (existing.recruitBanReason || 'noshow') : 'report',
  });
  addDoc(collection(db, 'notifications'), {
    uid,
    type: 'report_result',
    title: '⚠️ 모집글 작성 제한 안내',
    body: `신고 처리 결과에 따라 ${days}일간 모집글 작성이 제한됩니다.`,
    recruitId: null,
    read: false,
    createdAt: serverTimestamp(),
  }).catch(() => {});
  return { banUntil };
};

// ─── 75차: 총관리자가 제재를 수동으로 해제 ───────────────────────
export const liftRecruitBan = async (uid) => {
  await updateDoc(doc(db, 'users', uid), {
    recruitBanUntil: null,
    recruitBanReason: null,
  });
};

// ─── 내 알림 목록 조회 ───────────────────────────────────────
export const getMyNotifications = async (uid) => {
  const q = query(
    collection(db, 'notifications'),
    where('uid', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(50),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── 알림 읽음 처리 ─────────────────────────────────────────
export const markNotificationRead = async (notificationId) => {
  await updateDoc(doc(db, 'notifications', notificationId), { read: true });
};

// 78차 68차: "본사에 문의하기"/"고객 문의 답변" 등 배지가, 정작 그 화면(문의 내역/스레드)에
// 이미 들어가 답을 확인했는데도 안 없어지던 버그 — 이 화면들은 알림 목록(NotificationsScreen)
// 을 거치지 않고 바로 진입 가능해서, 알림 문서 자체가 read:false로 계속 남아 있었음.
// 해당 타입의 알림을 이 화면에 들어온 시점에 한꺼번에 읽음 처리.
export const markNotificationsReadByType = async (uid, types) => {
  if (!uid || !types?.length) return;
  const snap = await getDocs(query(
    collection(db, 'notifications'),
    where('uid', '==', uid),
    where('read', '==', false),
    where('type', 'in', types),
  ));
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.update(d.ref, { read: true }));
  await batch.commit();
};

// ─── 미읽은 알림 건수 조회 ───────────────────────────────────
const SYSTEM_NOTIF_TYPES = ['point_expiry', 'review_reminder', 'store_closed'];
// 72차: 매장관리자 전용 알림 타입 — NotificationsScreen이 이 타입만 매장관리자에게 보여준다.
// 78차 66차: new_store_inquiry 추가 — 고객 매장 문의 알림이 하단 탭 뱃지(adminOnly:true
// 필터)에 전혀 안 잡히던 버그(알림 자체는 왔었어도 뱃지에 안 세어지고 있었음).
// 78차 68차: inquiry_replied 추가 — 매장관리자도 "본사에 문의하기"로 문의를 보낸
// 당사자가 될 수 있는데, 이 타입이 목록에 없어서 MyPageScreen 자체 배지(1)엔 보여도
// 하단 탭 배지에는 전혀 안 잡히고 있었음(NotificationsScreen 목록에도 안 보이던 것 포함).
// 78차 49차: manual_entry_reminder/monthly_invoice_ready 추가 — 둘 다 매장관리자 전용으로
// functions/index.js가 이미 만들고 있는 알림인데 이 목록에 없어서 같은 유형의 버그(뱃지
// 미반영)가 반복되고 있었음.
// 79차: inquiry_followup 추가 — 문의자가 관리자 답변 뒤 후속 메시지를 남기면 담당
// 매장관리자/총괄관리자에게 가는 알림(functions/index.js의 onInquiryReplied 확장).
export const ADMIN_NOTIF_TYPES = ['new_reservation', 'new_review', 'reservation_cancelled_by_customer', 'digest_summary', 'new_store_inquiry', 'inquiry_replied', 'inquiry_followup', 'manual_entry_reminder', 'monthly_invoice_ready'];

// 72차: adminOnly 옵션 추가 — 매장관리자는 뱃지 숫자도 ADMIN_NOTIF_TYPES만 세야
// NotificationsScreen에 필터링된 목록과 뱃지 숫자가 어긋나 "안 없어지는 뱃지"가 안 생김.
export const getUnreadNotificationCount = async (uid, { adminOnly = false } = {}) => {
  const q = query(
    collection(db, 'notifications'),
    where('uid', '==', uid),
    where('read', '==', false),
  );
  const snap = await getDocs(q);
  return snap.docs.filter(d => {
    const type = d.data().type;
    return adminOnly ? ADMIN_NOTIF_TYPES.includes(type) : !SYSTEM_NOTIF_TYPES.includes(type);
  }).length;
};



// ─── 포인트 FIFO 차감 → CF-C usePointsFIFO_CF 래퍼 ──────────
// 53차: 잔액 검증 + FIFO를 서버 트랜잭션으로 처리
//   에러: 'INSUFFICIENT_POINTS' → 호출처에서 catch 후 사용자 안내
export const usePointsFIFO = async (uid, amount, reason) => {
  const cf = httpsCallable(functionsAsia, 'usePointsFIFO_CF');
  const result = await cf({ uid, amount, reason });
  return result.data;
};

// ─── paymentStatus pending 예약 조회 ────────────────────────
export const getMyPendingPaymentReservations = async (uid) => {
  const q = query(
    collection(db, 'reservations'),
    where('uid', '==', uid),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => r.cancelled !== true && r.paymentStatus === 'pending');
};

// ─── 내 모집글 중 신청 대기자가 있는 글 조회 ─────────────────
export const getMyRecruitsWithPending = async (uid) => {
  const q = query(
    collection(db, 'recruits'),
    where('uid', '==', uid),
  );
  const snap = await getDocs(q);
  const list = snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(r => (r.pendingParticipants?.length ?? 0) > 0);
  list.sort((a, b) => {
    const toMs = (v) => {
      if (!v) return 0;
      if (typeof v.toMillis === 'function') return v.toMillis();
      return new Date(v).getTime() || 0;
    };
    return toMs(b.createdAt) - toMs(a.createdAt);
  });
  return list;
};

// ─────────────────────────────────────────────────────────────
// ── 33차 추가 / 36차 Cloud Functions 이관 ────────────────────
// ─────────────────────────────────────────────────────────────

// ─── 포인트 만료 임박 인앱 알림 → Cloud Functions ─────────────
export const notifyExpiringPoints = async (uid) => {
  try {
    const cf = httpsCallable(functions, 'notifyExpiringPoints');
    await cf({ uid });
  } catch {
    // 무음 처리
  }
};

// ─── 리뷰 미작성 예약 유도 인앱 알림 → Cloud Functions ───────
export const notifyUnreviewedReservations = async (uid) => {
  try {
    const cf = httpsCallable(functions, 'notifyUnreviewedReservations');
    await cf({ uid });
  } catch {
    // 무음 처리
  }
};

// ─── 매장 soft-delete 시 예약자 일괄 알림 → Cloud Functions ──
export const notifyStoreDeleted = async (storeId, storeName) => {
  if (!storeId) return;
  try {
    const cf = httpsCallable(functions, 'notifyStoreDeleted');
    await cf({ storeId, storeName });
  } catch {
    // 무음 처리
  }
};

// ─────────────────────────────────────────────────────────────
// ── 52차: 폴링 → 실시간 리스너 전환 ─────────────────────────
// ─────────────────────────────────────────────────────────────

// ─── 미읽은 알림 건수 실시간 구독 ───────────────────────────
// 72차: adminOnly 옵션 추가 (getUnreadNotificationCount와 동일한 이유)
export const listenUnreadNotificationCount = (uid, callback, { adminOnly = false } = {}) => {
  if (!uid) return () => {};
  const q = query(
    collection(db, 'notifications'),
    where('uid',  '==', uid),
    where('read', '==', false),
  );
  return onSnapshot(
    q,
    (snap) => {
      const count = snap.docs.filter(d => {
        const type = d.data().type;
        return adminOnly ? ADMIN_NOTIF_TYPES.includes(type) : !SYSTEM_NOTIF_TYPES.includes(type);
      }).length;
      callback(count);
    },
    () => {},
  );
};

// ─── 내 모집글 대기자(pending) 총합 실시간 구독 ──────────────
export const listenMyRecruitsPendingCount = (uid, callback) => {
  if (!uid) return () => {};
  const q = query(
    collection(db, 'recruits'),
    where('uid', '==', uid),
  );
  return onSnapshot(
    q,
    (snap) => {
      const total = snap.docs.reduce(
        (acc, d) => acc + (d.data().pendingParticipants?.length || 0),
        0,
      );
      callback(total);
    },
    () => {},
  );
};

// 80차 계속: [발견성 개선, fork 감사로 발견] crew_mention 알림이 전체 안읽음 뱃지엔
// 이미 집계되고 있었지만(listenUnreadNotificationCount) 크루 탭 전용 뱃지가 없어서,
// 종모양 알림함을 안 열어보면 놓치기 쉬웠음(리텐션 훅으로 의도된 기능인데 발견성이
// 낮으면 효과가 줄어듦) — reservationAlerts와 동일한 타입별 전용 카운트 패턴.
export const listenUnreadCrewMentionCount = (uid, callback) => {
  if (!uid) return () => {};
  const q = query(
    collection(db, 'notifications'),
    where('uid',  '==', uid),
    where('read', '==', false),
    where('type', '==', 'crew_mention'),
  );
  return onSnapshot(q, (snap) => callback(snap.size), () => {});
};